
import { useContext, useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom"; // Added useNavigate

import Card from "@mui/material/Card";
import Checkbox from "@mui/material/Checkbox";
import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";


import FacebookIcon from "@mui/icons-material/Facebook";
import GoogleIcon from "@mui/icons-material/Google";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDAlert from "components/MDAlert";

import CoverLayout from "layouts/authentication/components/CoverLayout";
import bgImage from "assets/images/bg-sign-up-cover.jpeg";

import AuthService from "services/auth-service";
import WorkspaceService from "services/workspace-service";
import { AuthContext } from "context";
import { useSearchParams } from "react-router-dom";

function Register() {
  const authContext = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const invitationToken = searchParams.get("token");

  const [inputs, setInputs] = useState({
    name: "",
    email: "",
    password: "",
    agree: false,
  });

  const [errors, setErrors] = useState({
    nameError: false,
    emailError: false,
    passwordError: false,
    agreeError: false,
    error: false,
    errorText: "",
  });

  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [openConflictDialog, setOpenConflictDialog] = useState(false); // State for conflict dialog
  const [invitationDetails, setInvitationDetails] = useState(null);

  const changeHandler = (e) => {
    const { name, value, checked, type } = e.target;
    setInputs({
      ...inputs,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  useEffect(() => {
    if (invitationToken) {
      fetchInvitationDetails();
    }
  }, [invitationToken]);

  const fetchInvitationDetails = async () => {
    try {
      const details = await WorkspaceService.describeInvitation(invitationToken);
      setInvitationDetails(details);
      // Pre-fill email if provided in invitation
      if (details.email) {
        setInputs(prev => ({ ...prev, email: details.email }));
      }
    } catch (error) {
      console.error("Failed to fetch invitation details", error);
    }
  };

  const submitHandler = async (e) => {
    e.preventDefault();

    setErrors({
      nameError: false,
      emailError: false,
      passwordError: false,
      agreeError: false,
      error: false,
      errorText: "",
    });

    const mailFormat = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;

    if (inputs.name.trim().length === 0) {
      return setErrors((prev) => ({ ...prev, nameError: true }));
    }

    if (inputs.email.trim().length === 0 || !inputs.email.match(mailFormat)) {
      return setErrors((prev) => ({ ...prev, emailError: true }));
    }

    if (inputs.password.trim().length < 8) {
      return setErrors((prev) => ({ ...prev, passwordError: true }));
    }

    if (!inputs.agree) {
      return setErrors((prev) => ({ ...prev, agreeError: true }));
    }

    const newUser = {
      name: inputs.name,
      email: inputs.email,
      password: inputs.password,
    };

    const myData = {
      data: {
        type: "users",
        attributes: {
          ...newUser,
          password_confirmation: newUser.password,
          invitationToken // Pass token here
        },
        relationships: {
          roles: {
            data: [{ type: "roles", id: "1" }],
          },
        },
      },
    };

    try {
      setLoading(true);
      await AuthService.register(myData);

      // 🛑 Close Google One Tap if open
      if (window.google?.accounts?.id) {
        window.google.accounts.id.cancel();
      }

      setSuccessMessage(
        //"Your account has been successfully created. Please check your email and verify your account to continue."
        "Account created, check your email and verify account to continue."
      );

      setInputs({
        name: "",
        email: "",
        password: "",
        agree: false,
      });
    } catch (err) {

      // ✅ Handle Deleted Account Conflict (409)
      if (err?.errors?.[0]?.code === "ACCOUNT_DELETED") {
        setOpenConflictDialog(true);
        setLoading(false);
        return;
      }

      setErrors({
        nameError: false,
        emailError: false,
        passwordError: false,
        agreeError: false,
        error: true,
        errorText: err.message || "Registration failed.",
      });
    } finally {
      // Only stop loading if dialog is not opening (to avoid flash)
      if (!openConflictDialog) {
        setLoading(false);
      }
    }
  };

  const handleGoogleOneTapSuccess = async (credentialResponse) => {
    setLoading(true);
    try {
      const response = await AuthService.googleOneTapLogin(credentialResponse.credential, invitationToken);
      authContext.login(response.access_token, response.refresh_token);
    } catch (error) {
      console.error("Google One Tap Failed:", error);
      // Don't show global error for One Tap silent failure/close
      setLoading(false);
    }
  };

  // Manual Google One Tap Implementation
  useEffect(() => {
    const initializeGoogleOneTap = () => {
      if (!window.google || !window.google.accounts) {
        // console.log("GSI: Google script not loaded yet, retrying...");
        setTimeout(initializeGoogleOneTap, 500);
        return;
      }

      // console.log('GSI: Initializing Manual One Tap...');
      try {
        window.google.accounts.id.initialize({
          client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID?.trim(),
          callback: handleGoogleOneTapSuccess,
          auto_select: false,
          cancel_on_tap_outside: false,
          context: 'signup', // Changed context to 'signup' for Register page
          moment_callback: (notification) => {
            if (notification.getSkippedReason()) {
              console.warn('GSI: SKIPPED REASON:', notification.getSkippedReason());
            }
          }
        });

        window.google.accounts.id.prompt((notification) => {
          if (notification.isNotDisplayed()) {
            console.warn('GSI: Prompt not displayed:', notification.getNotDisplayedReason());
          } else if (notification.isSkippedMoment()) {
            console.warn('GSI: Prompt skipped:', notification.getSkippedReason());
          } else {
            // console.log('GSI: Prompt displayed successfully!');
          }
        });
      } catch (err) {
        console.error('GSI: Manual Initialization Error:', err);
      }
    };

    const timer = setTimeout(initializeGoogleOneTap, 1000);
    return () => {
      clearTimeout(timer);
      if (window.google?.accounts?.id) {
        window.google.accounts.id.cancel();
      }
    };
  }, []);

  const handleRedirectToLogin = () => {
    navigate("/auth/login", { state: { email: inputs.email } });
  };

  return (
    <CoverLayout image={bgImage}>
      <Card>
        <MDBox
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="success"
          mx={2}
          mt={-3}
          mb={1}
          pt={1}
          pb={invitationToken ? 2 : 1} // More padding if invited
          textAlign="center"
        >
          <MDTypography variant="h4" fontWeight="medium" color="white" mt={invitationToken ? 2 : 1}>
            {invitationToken ? (
              invitationDetails ? `Join ${invitationDetails.workspace_name}` : "Join Your Team"
            ) : "Start your free, 14 day trial"}
          </MDTypography>

          {!invitationToken && (
            <MDBox display="flex" flexDirection="row" alignItems="center" justifyContent="center" my={2} gap={3}>
              <MDBox display="flex" alignItems="center">
                <CheckCircleIcon sx={{ color: "white", fontSize: "1rem" }} />
                <MDTypography variant="button" color="white" ml={1} fontWeight="medium" sx={{ fontSize: "0.80rem" }}>
                  No Card Details
                </MDTypography>
              </MDBox>
              <MDBox display="flex" alignItems="center">
                <CheckCircleIcon sx={{ color: "white", fontSize: "1rem" }} />
                <MDTypography variant="button" color="white" ml={1} fontWeight="medium" sx={{ fontSize: "0.80rem" }}>
                  Cancel Anytime
                </MDTypography>
              </MDBox>
              <MDBox display="flex" alignItems="center">
                <CheckCircleIcon sx={{ color: "white", fontSize: "1rem" }} />
                <MDTypography variant="button" color="white" ml={1} fontWeight="medium" sx={{ fontSize: "0.80rem" }}>
                  No Contract
                </MDTypography>
              </MDBox>
            </MDBox>
          )}

          <MDBox mt={2} mb={1} display="flex" justifyContent="center">
            <MDButton
              variant="contained"
              color="info"
              onClick={() => AuthService.initiateFederatedLogin("google")}
              startIcon={<GoogleIcon fontSize="small" />}
              sx={{
                textTransform: "none",
                backgroundColor: "white !important",
                color: "#1f1f1f !important", // Google's specific dark gray
                borderColor: "#747775 !important", // Google's specific border gray
                borderRadius: "10px", // Pill shape
                padding: "8px 24px",  // Proper internal spacing
                fontSize: "14px",
                fontWeight: "500",
                width: "auto",        // Auto width (not full width)
                minWidth: "140px",    // Standard Google button min-width
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "white !important", // Keep white on hover
                  borderColor: "#747775 !important",
                  boxShadow: "0 1px 2px 0 rgba(60,64,67,0.3), 0 1px 3px 1px rgba(60,64,67,0.15) !important", // subtle Google-like shadow
                },
              }}
            >
              Google
            </MDButton>
          </MDBox>

          <MDBox display="flex" alignItems="center" justifyContent="center" width="100%" my={1} px={4}>
            <MDBox flex={1} sx={{ borderBottom: "1px solid white", mx: 1 }} />
            <MDTypography variant="button" color="white">
              or
            </MDTypography>
            <MDBox flex={1} sx={{ borderBottom: "1px solid white", mx: 1 }} />
          </MDBox>
        </MDBox>

        <MDBox pt={4} pb={3} px={3}>
          {/* {successMessage && (
            <MDAlert color="info" mt={2} dismissible onClose={() => setSuccessMessage("")}>
              <MDTypography variant="body2" color="white">
                {successMessage}
              </MDTypography>
            </MDAlert>
          )} */}

          <MDBox component="form" role="form" method="POST" onSubmit={submitHandler}>
            <MDBox mb={2}>
              <MDInput
                type="text"
                label="Full Name"
                variant="standard"
                fullWidth
                name="name"
                value={inputs.name}
                onChange={changeHandler}
                error={errors.nameError}
              />
              {errors.nameError && (
                <MDTypography variant="caption" color="error" fontWeight="light">
                  The name cannot be empty
                </MDTypography>
              )}
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                type="email"
                label="Email"
                variant="standard"
                fullWidth
                name="email"
                value={inputs.email}
                onChange={changeHandler}
                error={errors.emailError}
              />
              {errors.emailError && (
                <MDTypography variant="caption" color="error" fontWeight="light">
                  The email must be valid
                </MDTypography>
              )}
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                type="password"
                label="Password"
                variant="standard"
                fullWidth
                name="password"
                value={inputs.password}
                onChange={changeHandler}
                error={errors.passwordError}
              />
              {errors.passwordError && (
                <MDTypography variant="caption" color="error" fontWeight="light">
                  The password must be at least 8 characters
                </MDTypography>
              )}
            </MDBox>
            <MDBox display="flex" alignItems="center" ml={-1}>
              <Checkbox
                name="agree"
                id="agree"
                checked={inputs.agree}
                onChange={changeHandler}
                sx={{
                  color: "#6c757d",
                  "& .MuiSvgIcon-root": {
                    fontSize: 24,
                    border: inputs.agree ? "none" : "2px solid #adb5bd",
                    borderRadius: "4px",
                    fill: inputs.agree ? "currentColor" : "transparent"
                  },
                  "&.Mui-checked": { color: "info.main" }
                }}
              />
              <InputLabel htmlFor="agree" sx={{ cursor: "pointer", lineHeight: 1.5 }}>
                &nbsp;&nbsp;I agree to the&nbsp;
              </InputLabel>
              <MDTypography
                component="a"
                href={`${process.env.REACT_APP_URL_LANDING || "https://www.comparedly.com"}/terms`}
                target="_blank"
                rel="noopener noreferrer"
                variant="button"
                fontWeight="bold"
                color="info"
                textGradient
              >
                Terms and Conditions
              </MDTypography>
            </MDBox>
            {errors.agreeError && (
              <MDTypography variant="caption" color="error" fontWeight="light" sx={{ display: "block", textAlign: "left", mt: 1 }}>
                You must agree to the Terms and Conditions
              </MDTypography>
            )}

            {errors.error && errors.errorText !== "You must agree to the Terms and Conditions" && (
              <MDTypography variant="caption" color="error" fontWeight="light" sx={{ display: "block", textAlign: "left", mt: 1 }}>
                {errors.errorText}
              </MDTypography>
            )}
            {successMessage && (
              <MDAlert color="info" mt={2} dismissible onClose={() => setSuccessMessage("")}>
                <MDTypography variant="body2" color="white">
                  {successMessage}
                </MDTypography>
              </MDAlert>
            )}

            <MDBox mt={2} mb={1}>
              <MDButton variant="contained" color="accent" fullWidth type="submit" disabled={loading}>
                {loading ? <CircularProgress size={24} color="accent" /> : "Create Account"}
              </MDButton>
            </MDBox>

            <MDBox mt={0} mb={0} textAlign="center">
              <MDTypography variant="button" color="text">
                Already have an account?{" "}
                <MDTypography
                  component={Link}
                  to={invitationToken ? `/auth/login?token=${invitationToken}` : "/auth/login"}
                  variant="button"
                  color="info"
                  fontWeight="medium"
                  textGradient
                >
                  Sign In
                </MDTypography>
              </MDTypography>
            </MDBox>
            <MDBox mb={0} textAlign="center">
              <MDTypography variant="button" color="text">
                Go back to{" "}
                <MDTypography
                  component="a"
                  href={process.env.REACT_APP_URL_LANDING || "https://www.comparedly.com"}
                  variant="button"
                  color="info"
                  fontWeight="medium"
                  textGradient
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Comparedly
                </MDTypography>
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>

      {/* Reactivation / Conflict Dialog */}
      <Dialog
        open={openConflictDialog}
        onClose={() => setOpenConflictDialog(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"Account Deactivated"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            An account with this email address already exists but is currently deactivated.
            <br /><br />
            To reactivate it, please <strong>log in with your previous password</strong>.
            <br />
            (If you forgot it, you can reset it on the login page).
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConflictDialog(false)} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleRedirectToLogin} color="primary" autoFocus>
            Go to Login
          </Button>
        </DialogActions>
      </Dialog>
    </CoverLayout>
  );
}

export default Register;
